/**
 * Swift struct generator - Functional implementation with dependency injection
 * Generates Swift structs from interface definitions with Codable support
 */

import type { InterfaceDefinition, ParsedModule } from "../../parser";
import type { SwiftCodeEmitter } from "./swift-code-emitter";
import type { SwiftTypeMapper } from "./swift-type-mapper";

export interface SwiftStructGeneratorDeps {
  typeMapper: SwiftTypeMapper;
  codeEmitter: SwiftCodeEmitter;
}

export interface SwiftStructGenerator {
  /**
   * Generates all module types (Swift structs) from interface definitions
   */
  generateModuleTypes(module: ParsedModule, includeImports?: boolean): string;

  /**
   * Generates a Swift struct from interface definition for library packages
   * Includes public initializers and Codable conformance
   */
  generateLibrarySwiftStruct(
    interfaceDef: InterfaceDefinition,
    moduleName?: string,
    allInterfaces?: InterfaceDefinition[],
  ): string;

  /**
   * Generates a simple Swift struct without module prefix (for local use)
   */
  generateSwiftStruct(
    interfaceDef: InterfaceDefinition,
    allInterfaces?: InterfaceDefinition[],
  ): string;
}

/**
 * Creates a Swift struct generator with injected dependencies
 */
export function createSwiftStructGenerator(
  deps: SwiftStructGeneratorDeps,
): SwiftStructGenerator {
  const { typeMapper, codeEmitter } = deps;

  /**
   * Generates all module types (Swift structs) from interface definitions
   */
  function generateModuleTypes(
    module: ParsedModule,
    includeImports: boolean = true,
  ): string {
    // Generate Swift structs for interface definitions from AST
    const interfaceStructs = module.interfaceDefinitions
      .map((interfaceDef) =>
        generateLibrarySwiftStruct(
          interfaceDef,
          module.moduleName,
          module.interfaceDefinitions,
        ),
      )
      .join("\n\n");

    const moduleComment = `${module.moduleName} module types for Brick framework`;

    if (!includeImports) {
      return interfaceStructs;
    }

    return codeEmitter.generateSwiftFile(
      ["Foundation"],
      interfaceStructs,
      moduleComment,
    );
  }

  /**
   * Generates a Swift struct from interface definition for library packages
   * Includes public initializers and Codable conformance
   */
  function generateLibrarySwiftStruct(
    interfaceDef: InterfaceDefinition,
    moduleName?: string,
    allInterfaces: InterfaceDefinition[] = [],
  ): string {
    const properties = interfaceDef.properties
      .map((prop) => {
        const swiftType = typeMapper.mapTypeScriptToSwiftWithInterfaces(prop.type);
        const optional = prop.optional ? "?" : "";
        const comment = prop.jsDocComment
          ? `    ${codeEmitter.generateSwiftDocComment(prop.jsDocComment)}\n`
          : "";
        return `${comment}    ${codeEmitter.generateSwiftProperty(prop.name, `${swiftType}${optional}`, true, true)}`;
      })
      .join("\n");

    // Generate public initializer
    const initParams = interfaceDef.properties
      .map((prop) => {
        const swiftType = typeMapper.mapTypeScriptToSwiftWithInterfaces(prop.type);
        const optional = prop.optional ? "?" : "";
        return `${prop.name}: ${swiftType}${optional}`;
      })
      .join(", ");

    const initAssignments = interfaceDef.properties
      .map((prop) => `        self.${prop.name} = ${prop.name}`)
      .join("\n");

    // Add module prefix to struct name
    const structName = moduleName
      ? `${codeEmitter.capitalizeFirst(moduleName)}${interfaceDef.name}`
      : interfaceDef.name;

    return `public struct ${structName}: Codable {
${properties}

    public init(${initParams}) {
${initAssignments}
    }
}`;
  }

  /**
   * Generates a simple Swift struct without module prefix (for local use)
   */
  function generateSwiftStruct(
    interfaceDef: InterfaceDefinition,
    allInterfaces: InterfaceDefinition[] = [],
  ): string {
    const properties = interfaceDef.properties.map((prop) => {
      const swiftType = typeMapper.mapTypeScriptToSwiftWithInterfaces(prop.type);
      const optional = prop.optional ? "?" : "";
      return {
        name: prop.name,
        type: `${swiftType}${optional}`,
        comment: prop.jsDocComment,
      };
    });

    return codeEmitter.generateSwiftStruct(
      interfaceDef.name,
      properties,
      ["Codable"],
      false,
    );
  }

  return {
    generateModuleTypes,
    generateLibrarySwiftStruct,
    generateSwiftStruct,
  };
}
